import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { ACTION_ORDER_PLAN_START_UPDATE } from '@retainerclub/admin-api';
import { BaseLoader, ErrorMessage, SubmitButton } from '@retainerclub/common';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';

const schema = () =>
  yup.object({
    planStartDelay: yup.date().nullable(),
  });

const initialValues = ({ record }) => ({
  planStartDelay: record?.planStartDelay || null,
});

function OrderPlanStartModalWrapper({
  order = null,
  handleCloseModal,
  children,
}) {
  const [mutationErrors, setMutationErrors] = useState(null);

  const [update, { error, loading }] = useMutation(
    ACTION_ORDER_PLAN_START_UPDATE,
    {
      refetchQueries: ['order'],
      awaitRefetchQueries: true,
    },
  );

  const memoizedInitialValues = useMemo(
    () => initialValues({ record: order }),
    [order],
  );

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, isValid },
    formState,
    getValues,
    setValue,
    control,
    trigger,
    ...rest
  } = useForm({
    resolver: yupResolver(schema()),
    defaultValues: memoizedInitialValues,
    values: memoizedInitialValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  async function submitRecord(values) {
    setMutationErrors(null);

    try {
      // Convert Date object to ISO string for GraphQL
      const planStartDelayValue =
        values.planStartDelay instanceof Date
          ? values.planStartDelay.toISOString()
          : values.planStartDelay || null;

      const response = await update({
        variables: {
          input: {
            input: {
              id: order.id,
              planStartDelay: planStartDelayValue,
            },
            clientMutationId: uuidv4(),
          },
        },
      });

      if (response.data.actionOrderPlanStartUpdate.errorMessages.length > 0) {
        setMutationErrors(
          response.data.actionOrderPlanStartUpdate.errorMessages,
        );
      } else {
        handleCloseModal();
      }
    } catch (err) {
      console.error('Update error', err);
      setMutationErrors([{ message: 'An error occurred while updating.' }]);
    }
  }

  async function handleClearDate() {
    setMutationErrors(null);

    try {
      const response = await update({
        variables: {
          input: {
            input: {
              id: order.id,
              planStartDelay: null,
            },
            clientMutationId: uuidv4(),
          },
        },
      });

      if (response.data.actionOrderPlanStartUpdate.errorMessages.length > 0) {
        setMutationErrors(
          response.data.actionOrderPlanStartUpdate.errorMessages,
        );
      } else {
        handleCloseModal();
      }
    } catch (err) {
      console.error('Clear date error', err);
      setMutationErrors([
        { message: 'An error occurred while clearing the date.' },
      ]);
    }
  }

  return (
    <FormProvider
      register={register}
      handleSubmit={handleSubmit}
      formState={formState}
      getValues={getValues}
      setValue={setValue}
      control={control}
      trigger={trigger}
      {...rest}
    >
      <form
        onSubmit={handleSubmit(submitRecord)}
        className="flex min-h-[500px] flex-col justify-between"
      >
        <div>
          {loading && <BaseLoader />}
          <ErrorMessage errors={mutationErrors || error} />
          {children}
        </div>
        <div className="flex justify-end gap-3 pt-8">
          {order?.planStartDelay && (
            <button
              type="button"
              onClick={handleClearDate}
              disabled={loading}
              className="rounded-xl bg-white px-3 py-2 font-semibold text-red-600 text-sm shadow-xs ring-1 ring-red-300 ring-inset hover:bg-red-50 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Clear Date
            </button>
          )}
          <SubmitButton
            disabled={!(isValid && getValues('planStartDelay'))}
            isSubmitting={isSubmitting || loading}
            label="Save"
          />
        </div>
      </form>
    </FormProvider>
  );
}

OrderPlanStartModalWrapper.propTypes = {
  order: PropTypes.object.isRequired,
  handleCloseModal: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

export default OrderPlanStartModalWrapper;
